import React from "react";
import { PostRequestResolve } from "../../../../../utils/FetchHelper";
import { Alert, Card, DatePicker, Flex, Space, Statistic, Tooltip } from "antd";
import URL from "../../../../../utils/url";
import _ from "lodash";
import Title from "antd/es/typography/Title";
import dayjs from 'dayjs';
import { DownloadOutlined } from "@ant-design/icons";
const { RangePicker } = DatePicker;

const AnalyticMetricType = [
    "GENERATE_DISCOVERY_REPORT",
    "GENERATE_THREAT_REPORT"
]

const AnalyticMetricText = {
    GENERATE_DISCOVERY_REPORT: "Discovery Report",
    GENERATE_THREAT_REPORT: "Threat Report"
}

const IntervalType = {
    DAY: "Daily",
    WEEK: "Weekly",
    MONTH: "Monthly",
    YEAR: "Yearly",
    QUARTER: "Quarterly",
}

const rangePresets = [
    {
        label: 'Last 1 Day',
        value: [dayjs().add(-1, 'd'), dayjs()],
    },
    {
        label: 'Last 1 Week',
        value: [dayjs().add(-1, 'w'), dayjs()],
    },
    {
        label: 'Last 1 Month',
        value: [dayjs().add(-1, 'M'), dayjs()],
    },
    {
        label: 'Last 1 Year',
        value: [dayjs().add(-30, 'y'), dayjs()],
    },
];

// AnalyticMetric Summary React Functional Component
function AnalyticMetricSummary({ acctName }) {
    // threat report summary
    const [reportSummary, setReportSummary] = React.useState([]);
    const [reportLoading, setReportLoading] = React.useState(true);
    const [reportError, setReportError] = React.useState(null);

    // unique users summary
    const [uniqueUsersSummary, setUniqueUsersSummary] = React.useState([]);
    const [uniqueUsersLoading, setUniqueUsersLoading] = React.useState(true);
    const [uniqueUsersError, setUniqueUsersError] = React.useState(null);

    // range state
    const [range, setRange] = React.useState(null);

    // load Analytic Metics Summary on start
    React.useEffect(() => {
        const controller = new window.AbortController();
        const startTime = _.get(range, "[0]");
        const endTime = _.get(range, "[1]");

        // set loading
        setReportLoading(true);
        setUniqueUsersLoading(true);

        // Threat Report Summary
        Promise.all(_.map(AnalyticMetricType, (type) => PostRequestResolve(URL.getAnalyticMetricsSummary, controller.signal, {
            acctName: acctName,
            type: type,
            startTime: startTime,
            endTime: endTime
        }))).then((responses) => {
            // check no data
            const total = _.sumBy(responses, (response) => _.isArray(response) ? response.length : 0);
            if (total === 0) {
                setReportError({ message: "No data found" });
                setReportSummary([]);
            } else {
                setReportSummary(responses);
            }
            setReportLoading(false);
        }).catch((error) => {
            setReportError(error);
            console.error("Failed to load report summary", error);
        });

        // Unique Users Summary
        Promise.all(_.map(AnalyticMetricType, (type) => PostRequestResolve(URL.getAnalyticMetricsSummary, controller.signal, {
            acctName: acctName,
            type: type,
            startTime: startTime,
            endTime: endTime,
            uniqueUsers: true
        }))).then((responses) => {
            // check no data
            const total = _.sumBy(responses, (response) => _.isArray(response) ? response.length : 0);
            if (total === 0) {
                setUniqueUsersError({ message: "No data found" });
                setUniqueUsersSummary([]);
            } else {
                setUniqueUsersSummary(responses);
            }
            setUniqueUsersLoading(false);
        }).catch((error) => {
            setUniqueUsersLoading(false);
            setUniqueUsersError(error);
            console.error("Failed to load unique users summary", error);
        });


        return () => {
            controller.abort();
        }
    }, [acctName, range]);

    // onRangeChange function
    const onRangeChange = (dates) => {
        setRange(dates);
    }

    return (
        <>
            <Space style={{ width: "100%" }}>
                <Tooltip title="Clear for all the dates from when data is available" placement="topLeft">Select Date Range</Tooltip>
                <RangePicker
                    presets={rangePresets}
                    showTime
                    format="YYYY/MM/DD HH:mm:ss"
                    onChange={onRangeChange}
                />
            </Space>
            {_.map([
                {
                    key: "downloads",
                    title: "Total Downloads",
                    data: reportSummary,
                    loading: reportLoading,
                    error: reportError,
                    tooltip: "Total number of reports downloaded",
                    defaultError: "Failed to load report summary"
                },
                {
                    key: "users",
                    title: "Downloads by Unique Users",
                    data: uniqueUsersSummary,
                    loading: uniqueUsersLoading,
                    error: uniqueUsersError,
                    tooltip: "Total number of unique users who downloaded the report",
                    defaultError: "Failed to load unique users summary"
                }
            ], (item, i) => (
                <>
                    <Tooltip title={item.tooltip} placement="topLeft">
                        <Title level={5} style={{ marginTop: 8 }}>{item.title}</Title>
                    </Tooltip>
                    {item.error ? <Alert message={_.get(item.error, "message", item.defaultError)} type="error" showIcon /> : (
                        <Flex gap={8} wrap>
                            {_.map(IntervalType, (interval_text, interval_type) => (
                                <Card size="small" key={`${item.key}${interval_type}`} style={{ minWidth: "200px", padding: "8px" }}>
                                    <Statistic
                                        title={`Total - ${interval_text}`}
                                        value={_.sumBy(item.data, (report) => _.get(_.find(report, { type: interval_type }), item.key, 0))}
                                        loading={item.loading}
                                        prefix={<DownloadOutlined />}
                                    />
                                    {_.map(AnalyticMetricType, (type, i) => (
                                        <Statistic
                                            key={`${type}-${i}`}
                                            title={`${AnalyticMetricText[type]}`}
                                            value={_.get(_.find(item.data[i], { type: interval_type }), item.key, "-")}
                                            loading={item.loading}
                                        />
                                    ))}
                                </Card>
                            ))
                            }
                        </Flex>
                    )}
                </>
            ))}
        </>
    );
}

export default AnalyticMetricSummary;